import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { entries, branches, talksByEntry } from "./timelineData";

const LANE_X = [18, 42, 66]; // x of each lane inside the graph column
const CURVE = 16; // half height of a fork / merge curve
const NODE_R = 4;
const NOTES_SHOWN = 4; // margin notes per milestone before "Show more", more where there is room
const NOTE_DRIFT = 240; // how far notes may slide below their milestone to fill the space

const isMobile = () => window.matchMedia("(max-width: 640px)").matches;

// The rose's stem ends at the start of `.svg-elem-3` and points down-left from there
const ROSE_TIP = { x: 292.954, y: 905.117 };
const ROSE_DIR = { x: -0.658, y: 0.753 };

// Grow the rose's stem on the way a real one hangs: its last stroke carries straight on, then one
// wide, even bend lets it droop into the main lane, ending in the first commit's node, which hides
// the handover. Drawn inside the rose's own svg (in its units), so it joins the rose seamlessly.
const pt = (p) => `${p.x} ${p.y}`;

// The rose's own leaves, reused along the stem: where each meets the rose's stem and the way it points
const LEAVES = {
  right: { paths: [".svg-elem-1", ".svg-elem-2"], base: { x: 423.5, y: 552 }, angle: -43.8, box: [424, 336, 650, 583] },
  left: { paths: [".svg-elem-4"], base: { x: 343.2, y: 631.5 }, angle: -157.2, box: [67, 515, 350, 741] },
};
// how far down the stem (share of its length), which side, and how big next to the rose's own leaves
const STEM_LEAVES = [
  // the big one opens wider, so it reaches out to the left like the rose's own left leaf
  { at: 0.42, side: "left", scale: 0.62, spread: 92 },
  { at: 0.66, side: "right", scale: 0.46 },
  { at: 0.9, side: "left", scale: 0.3 },
];
const LEAF_SPREAD = 38; // degrees between the stem and a leaf, as on the rose itself
const LEAF_GAP = 70; // space between the stem and a leaf, in the rose's units at full size

// The stem carries on from the rose's tip in one soft sweep, turns straight down onto the main
// lane and runs down to the first commit. Three of the rose's leaves grow off it.
const buildStem = (s) => {
  const tip = ROSE_TIP;
  const land = {
    x: s.laneX,
    y: Math.min(s.nodeY, tip.y + (tip.x - s.laneX) * 2.2),
  };
  const c = Math.hypot(land.x - tip.x, land.y - tip.y);
  const c1 = { x: tip.x + ROSE_DIR.x * c * 0.4, y: tip.y + ROSE_DIR.y * c * 0.4 };
  const c2 = { x: land.x, y: land.y - c * 0.5 };
  const at = (t) => {
    const m = 1 - t;
    const f = (k) =>
      m * m * m * tip[k] + 3 * m * m * t * c1[k] + 3 * m * t * t * c2[k] + t * t * t * land[k];
    return { x: f("x"), y: f("y") };
  };

  // points along the whole stem (the sweep, then the straight run down), with how far along each is
  const samples = [];
  for (let i = 0; i <= 100; i++) samples.push(at(i / 100));
  for (let i = 1; i <= 20; i++)
    samples.push({ x: land.x, y: land.y + ((s.nodeY - land.y) * i) / 20 });
  const lengths = [0];
  for (let i = 1; i < samples.length; i++)
    lengths.push(
      lengths[i - 1] +
        Math.hypot(samples[i].x - samples[i - 1].x, samples[i].y - samples[i - 1].y),
    );
  const total = lengths[lengths.length - 1];

  const leaves = STEM_LEAVES.map(({ at: share, side, scale: size, spread: wide = LEAF_SPREAD }) => {
    const leaf = LEAVES[side];
    const i = Math.max(1, lengths.findIndex((l) => l >= share * total));
    const p = samples[i];
    const prev = samples[i - 1];
    // pointing back up the stem, then turned out to its side
    const up = (Math.atan2(prev.y - p.y, prev.x - p.x) * 180) / Math.PI;
    // like the rose's own leaves, they float a little off the stem
    const place = (spread, scale) => {
      const out = up + (side === "right" ? spread : -spread);
      const rad = (out * Math.PI) / 180;
      const gap = LEAF_GAP * scale;
      const base = { x: p.x + Math.cos(rad) * gap, y: p.y + Math.sin(rad) * gap };
      // where the corners of the leaf's outline end up, turned and scaled into place
      const turn = ((out - leaf.angle) * Math.PI) / 180;
      const [x0, y0, x1, y1] = leaf.box;
      const xs = [
        [x0, y0],
        [x1, y0],
        [x0, y1],
        [x1, y1],
      ].map(
        ([x, y]) =>
          base.x +
          ((x - leaf.base.x) * Math.cos(turn) - (y - leaf.base.y) * Math.sin(turn)) * scale,
      );
      const fits = Math.min(...xs) >= s.left && Math.max(...xs) <= s.right;
      return { out, base, scale, fits };
    };
    // On narrow screens a leaf turns closer to the stem, then shrinks, until it stays on screen
    let placed = place(wide, size);
    for (let spread = wide; !placed.fits && spread > 10; spread -= 4) placed = place(spread, size);
    for (let scale = size; !placed.fits && scale > size * 0.5; scale *= 0.92) placed = place(10, scale);
    const { out, base, scale } = placed;
    return {
      paths: leaf.paths,
      scale,
      transform:
        `translate(${base.x} ${base.y}) rotate(${out - leaf.angle}) scale(${scale})` +
        ` translate(${-leaf.base.x} ${-leaf.base.y})`,
      delay: share,
    };
  });

  return {
    d:
      `M ${pt(tip)} C ${pt(c1)}, ${pt(c2)}, ${pt(land)}` +
      ` V ${s.nodeY}`,
    leaves,
  };
};

// Build the graph paths from the measured y position of every commit
const buildPaths = (ys) => {
  const bottom = ys[entries[entries.length - 1].id];
  const top = ys[entries[0].id];
  const x0 = LANE_X[0];

  // A fork sits halfway into the gap below the row it leads to
  const below = (id) => {
    const i = entries.findIndex((e) => e.id === id);
    return (ys[id] + ys[entries[i + 1].id]) / 2;
  };

  const paths = [`M ${x0} ${bottom} V ${ys[entries[0].id]}`];
  branches.forEach((b) => {
    const x = LANE_X[b.lane];
    const f = below(b.from);
    let d = `M ${x0} ${f + CURVE} C ${x0} ${f}, ${x} ${f}, ${x} ${f - CURVE}`;
    if (b.mergeInto) {
      // run up the branch lane, then curve into the merge commit
      const m = ys[b.mergeInto];
      d += ` V ${m + 2 * CURVE} C ${x} ${m + CURVE}, ${x0} ${
        m + CURVE
      }, ${x0} ${m}`;
    } else {
      d += ` V ${top}`;
    }
    paths.push(d);
  });
  return paths;
};

// One talk, podcast or appearance: date and format, then where, then what, linking to the source
const Talk = ({ talk, index, compact, hidden }) => (
  <li className={"talk" + (hidden ? " talkHidden" : "")} style={{ "--i": index }}>
    <a
      href={talk.url}
      target="_blank"
      rel="noopener noreferrer"
      title={compact ? talk.place : undefined}
    >
      <span className="talkMeta">
        {talk.period} · {talk.format}
      </span>
      <span className="talkTitle">{talk.title}</span>
      {!compact && <span className="talkAbout">{talk.place}</span>}
    </a>
  </li>
);

const Timeline = () => {
  const listRef = useRef(null);
  const revealRef = useRef(null);
  // The lanes only start drawing once the stem has grown out of the rose
  const stemDone = useRef(
    window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  const revealed = useRef(0);
  const [graph, setGraph] = useState(null);
  const [rose, setRose] = useState(null);
  // milestones whose margin notes are all shown
  const [expanded, setExpanded] = useState({});
  // how many notes fit beside each milestone before the next one's notes start
  const [fit, setFit] = useState({});

  const measure = useCallback(() => {
    const list = listRef.current;
    const rose = document.querySelector(".lion");
    if (!list || !rose) return;
    const listRect = list.getBoundingClientRect();

    // The graph svg's origin is the tip of the rose's stem
    const ctm = rose.getScreenCTM();
    const toRoseX = (x) => (x - ctm.e) / ctm.a;
    const toRoseY = (y) => (y - ctm.f) / ctm.d;
    const tipY = ctm.b * ROSE_TIP.x + ctm.d * ROSE_TIP.y + ctm.f;
    const offset = listRect.top - tipY;

    const graphWidth = parseFloat(
      getComputedStyle(list).getPropertyValue("--graph-width"),
    );
    const content = list.querySelector(".timelineContent");
    // Snap the graph to whole pixels so its lines meet the stem without a sub-pixel jog
    const rawLeft = content.getBoundingClientRect().left - graphWidth;
    const graphLeft = Math.round(rawLeft);

    const mobile = isMobile();
    const ys = {};
    list.querySelectorAll(".timelineItem").forEach((row) => {
      const anchor = row.querySelector(
        mobile ? ".timelinePeriod" : ".timelineTitle",
      );
      const r = anchor.getBoundingClientRect();
      ys[row.dataset.id] = Math.round(r.top + r.height / 2 - tipY);
    });
    // The stem fades out around the name and title instead of crossing them: one box per line of
    // text, tight to the letters, measured without the fade-in's offset
    const cuts = [".hero h1", ".hero h2"].map((selector) => {
      const el = document.querySelector(selector);
      const style = getComputedStyle(el);
      const em = parseFloat(style.fontSize);
      const shift = style.transform === "none" ? 0 : new DOMMatrix(style.transform).m42;
      const text = document.createRange();
      text.selectNodeContents(el);
      const r = text.getBoundingClientRect();
      // Lato's line box is 1.2em with the baseline at 0.987em; capitals start about 0.27em down
      const top = r.top - shift + (r.height - 1.2 * em) / 2;
      const pad = 0.16 * em;
      const x = toRoseX(r.left - pad);
      const y = toRoseY(top + 0.27 * em - pad);
      return {
        x,
        y,
        width: toRoseX(r.right + pad) - x,
        height: toRoseY(top + 0.987 * em + pad) - y,
        radius: pad / ctm.a,
      };
    });
    const stem = {
      laneX: toRoseX(graphLeft + LANE_X[0]),
      nodeY: toRoseY(tipY + ys[entries[0].id]),
      // the leaves stay this far inside the screen
      left: toRoseX(12),
      right: toRoseX(document.documentElement.clientWidth - 12),
    };
    const built = buildStem(stem);
    setGraph({
      ys,
      offset,
      nudge: graphLeft - rawLeft,
      // same on-screen line width as the rose's 1-unit strokes
      lineWidth: ctm.a,
      width: LANE_X[LANE_X.length - 1] + 24,
      height: list.offsetHeight + offset,
      stem: built.d,
      leaves: built.leaves,
      cuts,
      // the fade at the edge of a cut, a few pixels wide
      cutBlur: 3 / ctm.a,
      paths: buildPaths(ys),
    });
    setRose(rose);
  }, []);

  useLayoutEffect(() => {
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(listRef.current);
    // The re-measure re-renders the graph, which restarts the reveal effect below. Once the stem
    // has grown, that restart lets the lanes follow.
    const onAnimationEnd = (event) => {
      if (event.target.classList.contains("timelineStem"))
        stemDone.current = true;
      measure();
    };
    window.addEventListener("resize", measure);
    document.addEventListener("animationend", onAnimationEnd);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
      document.removeEventListener("animationend", onAnimationEnd);
    };
  }, [measure]);

  useEffect(() => {
    const list = listRef.current;

    // Reveal each commit once it scrolls into view
    const observer = new IntersectionObserver(
      (items) => {
        items.forEach((item) => {
          if (item.isIntersecting) {
            item.target.classList.add("inView");
            list
              .querySelector(
                `.timelineNode[data-id="${item.target.dataset.id}"]`,
              )
              ?.classList.add("inView");
            observer.unobserve(item.target);
          }
        });
      },
      { rootMargin: "0px 0px -20% 0px" },
    );
    list
      .querySelectorAll(".timelineItem")
      .forEach((row) => observer.observe(row));

    // Draw the lanes down as you scroll, easing towards the scroll position like a pen following it
    let frame = null;
    const update = () => {
      frame = null;
      if (!graph || !revealRef.current) return;
      const svgTop = list.getBoundingClientRect().top - graph.offset;
      // once the stem has grown, the line always carries on out of the first node
      const target = stemDone.current
        ? Math.max(
            window.innerHeight * 0.75 - svgTop,
            graph.ys[entries[0].id] + 120,
          )
        : 0;
      const diff = target - revealed.current;
      revealed.current =
        Math.abs(diff) < 0.5 ? target : revealed.current + diff * 0.08;
      revealRef.current.setAttribute("height", revealed.current);
      if (revealed.current !== target) frame = requestAnimationFrame(update);
    };
    const onScroll = () => {
      if (frame === null) frame = requestAnimationFrame(update);
    };
    // Start over when the rose is redrawn: hide everything, then grow the stem again
    const onRedraw = () => {
      const stemPath = document.querySelector(".timelineStem");
      if (!stemPath || getComputedStyle(stemPath).animationName === "none")
        return;
      stemDone.current = false;
      revealed.current = 0;
      revealRef.current.setAttribute("height", 0);
      stemPath.style.animation = "none";
      setTimeout(() => {
        stemPath.style.animation = "";
      }, 60);
    };
    update();
    window.addEventListener("rose:redraw", onRedraw);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      observer.disconnect();
      window.removeEventListener("rose:redraw", onRedraw);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [graph]);

  // Margin notes start level with their milestone, and move down when the notes above still run.
  // Each group shows as many notes as fit before the next group starts, at least NOTES_SHOWN.
  useLayoutEffect(() => {
    if (!graph) return;
    const list = listRef.current;
    list.style.paddingBottom = "";
    // hidden on narrow screens
    const groups = [...list.querySelectorAll(".timelineAside ul")].filter(
      (notes) => notes.offsetParent,
    );
    // lay every note out to measure it, level with its milestone
    groups.forEach((notes) => {
      notes.style.transform = "";
      notes.classList.add("measuring");
    });
    const tops = groups.map((notes) => notes.getBoundingClientRect().top);
    const end = list.getBoundingClientRect().bottom;
    // each group's height with n of its notes shown
    const sizes = groups.map((notes, g) => {
      const talks = [...notes.querySelectorAll(".talk")];
      const ends = talks.map((t) => t.getBoundingClientRect().bottom - tops[g]);
      const more = notes.querySelector(".talkMore");
      const moreHeight = more
        ? more.getBoundingClientRect().bottom - tops[g] - ends[ends.length - 1]
        : 0;
      const open = !!expanded[notes.dataset.id];
      return {
        total: talks.length,
        least: open ? talks.length : Math.min(NOTES_SHOWN, talks.length),
        heightOf: (n) => (open || n < talks.length ? ends[n - 1] + moreHeight : ends[n - 1]),
      };
    });
    const counts = sizes.map((size) => size.least);
    // where each group ends up with the current counts, and where the last one ends
    const place = () => {
      let bottom = -Infinity;
      const shifts = sizes.map((size, g) => {
        const shift = Math.max(0, bottom + 28 - tops[g]);
        bottom = tops[g] + shift + size.heightOf(counts[g]);
        return shift;
      });
      return { shifts, bottom };
    };
    const { shifts: least, bottom: leastBottom } = place();
    // Groups take turns showing one more note while that keeps every group within NOTE_DRIFT of
    // its milestone (or where it has to be anyway), filling the quiet stretches evenly
    for (let grew = true; grew; ) {
      grew = false;
      sizes.forEach((size, g) => {
        if (counts[g] === size.total) return;
        counts[g]++;
        const tried = place();
        if (
          tried.shifts.some((d, k) => d > Math.max(NOTE_DRIFT, least[k])) ||
          tried.bottom > Math.max(end, leastBottom)
        )
          counts[g]--;
        else grew = true;
      });
    }
    const { shifts, bottom } = place();
    groups.forEach((notes, g) => {
      notes.classList.remove("measuring");
      notes.style.transform = shifts[g] ? `translateY(${shifts[g]}px)` : "";
    });
    setFit((prev) =>
      groups.every((notes, g) => prev[notes.dataset.id] === counts[g])
        ? prev
        : Object.fromEntries(groups.map((notes, g) => [notes.dataset.id, counts[g]])),
    );
    // keep the page long enough for notes that run past the last milestone
    const overflow = bottom - list.getBoundingClientRect().bottom;
    list.style.paddingBottom = overflow > 0 ? `${overflow}px` : "";
  }, [graph, expanded, fit]);

  const limitOf = (id) => fit[id] || NOTES_SHOWN;
  // Every note stays in the page to be measured, those past the limit hidden. A year is marked
  // where the shown notes cross into a new one, so a group continues from the last note shown.
  const notes = {};
  let noteYear = null;
  entries.forEach((e) => {
    if (!talksByEntry[e.id]) return;
    let year = noteYear;
    notes[e.id] = talksByEntry[e.id].map((t, i) => {
      const hidden = !expanded[e.id] && i >= limitOf(e.id);
      const newYear = t.date.slice(0, 4) !== year;
      year = t.date.slice(0, 4);
      if (!hidden) noteYear = year;
      return { t, i, hidden, newYear };
    });
  });
  return (
    <section className="timeline" aria-label="Timeline">
      <div className="timelineList" ref={listRef}>
        {graph && (
          <svg
            className="timelineGraph"
            width={graph.width}
            height={graph.height}
            style={{
              top: -graph.offset,
              marginLeft: graph.nudge,
              "--line-width": graph.lineWidth,
            }}
            aria-hidden="true"
          >
            <defs>
              <clipPath id="timelineReveal">
                <rect
                  ref={revealRef}
                  x="-5000"
                  y="0"
                  width="10000"
                  height="0"
                />
              </clipPath>
            </defs>
            <g clipPath="url(#timelineReveal)">
              {graph.paths.map((d) => (
                <path key={d} d={d} className="timelineLane" />
              ))}
            </g>
            {entries.map((e) => (
              <circle
                key={e.id}
                data-id={e.id}
                className={"timelineNode" + (e.current ? " current" : "")}
                cx={LANE_X[e.lane]}
                cy={graph.ys[e.id]}
                r={NODE_R}
              />
            ))}
          </svg>
        )}
        {graph &&
          rose &&
          createPortal(
            <>
              <defs>
                <filter id="stemCutSoft" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation={graph.cutBlur} />
                </filter>
                <mask id="stemCut" maskUnits="userSpaceOnUse" x="-5000" y="-5000" width="10000" height="10000">
                  <rect x="-5000" y="-5000" width="10000" height="10000" fill="white" />
                  <g filter="url(#stemCutSoft)">
                    {graph.cuts.map((c, i) => (
                      <rect key={i} x={c.x} y={c.y} width={c.width} height={c.height} rx={c.radius} fill="black" />
                    ))}
                  </g>
                </mask>
              </defs>
              <g mask="url(#stemCut)">
                <path d={graph.stem} pathLength="1" className="timelineStem" />
                {graph.leaves.map((leaf, i) => (
                  <g
                    key={i}
                    className="timelineLeaf"
                    style={{
                      "--leaf-delay": leaf.delay,
                      "--leaf-width": `calc(var(--line-width, 1) / ${leaf.scale})`,
                    }}
                  >
                    <g transform={leaf.transform}>
                      {leaf.paths.map((selector) => (
                        <path
                          key={selector}
                          d={rose.querySelector(selector).getAttribute("d")}
                          pathLength="1"
                          className="timelineLeafShape"
                        />
                      ))}
                    </g>
                  </g>
                ))}
              </g>
            </>,
            rose,
          )}
        <ol className="timelineRows">
          {entries.map((e) => (
            <li key={e.id} data-id={e.id} className="timelineItem">
              <div className="timelinePeriod">{e.period}</div>
              <div className="timelineContent">
                <div className="timelineTitle">{e.title}</div>
                <div className="timelinePlace">
                  {e.url ? (
                    <a href={e.url} target="_blank" rel="noopener noreferrer">
                      {e.place}
                    </a>
                  ) : (
                    e.place
                  )}
                </div>
              </div>
              {talksByEntry[e.id] && (
                <aside
                  className="timelineAside"
                  aria-label={`Talks, media & more during ${e.title}, ${e.place}`}
                >
                  <ul data-id={e.id}>
                    {notes[e.id].map(({ t, i, hidden, newYear }) => (
                      <React.Fragment key={t.id}>
                        {newYear && (
                          <li
                            className={"talkYear" + (hidden ? " talkHidden" : "")}
                            aria-hidden="true"
                          >
                            {t.date.slice(0, 4)}
                          </li>
                        )}
                        <Talk talk={t} index={i} compact hidden={hidden} />
                      </React.Fragment>
                    ))}
                    {/* kept in the page while every note fits, so it can be measured */}
                    {talksByEntry[e.id].length > NOTES_SHOWN && (
                      <li
                        className={
                          !expanded[e.id] &&
                          talksByEntry[e.id].length <= limitOf(e.id)
                            ? "talkHidden"
                            : undefined
                        }
                      >
                        <button
                          type="button"
                          className="talkMore"
                          aria-expanded={!!expanded[e.id]}
                          onClick={() =>
                            setExpanded((x) => ({ ...x, [e.id]: !x[e.id] }))
                          }
                        >
                          {expanded[e.id]
                            ? "Show less"
                            : `Show ${
                                talksByEntry[e.id].length - limitOf(e.id)
                              } more`}
                        </button>
                      </li>
                    )}
                  </ul>
                </aside>
              )}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

export default Timeline;
